import { Transaction } from "../../../../services/transactions";
import { TransactionType } from "../../../../utils";
import { AmountVariant, DateGroup } from "./types";

const ACCOUNT_DOT_PALETTE = [
  "var(--color-warning)",
  "var(--color-success)",
  "var(--color-info)",
  "var(--color-error)",
  "var(--color-text-secondary)",
  "var(--color-text-muted)",
];

export const getAccountDotColor = (
  name: string | undefined,
  accountId?: number,
): string => {
  if (!name && accountId === undefined) {
    return ACCOUNT_DOT_PALETTE[ACCOUNT_DOT_PALETTE.length - 1];
  }
  if (accountId !== undefined) {
    return ACCOUNT_DOT_PALETTE[accountId % ACCOUNT_DOT_PALETTE.length];
  }
  if (!name) {
    return ACCOUNT_DOT_PALETTE[ACCOUNT_DOT_PALETTE.length - 1];
  }
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return ACCOUNT_DOT_PALETTE[hash % ACCOUNT_DOT_PALETTE.length];
};

export const getDailyNet = (transactions: Transaction[]): number => {
  return transactions.reduce((sum, t) => {
    if (t.transactionType === TransactionType.INCOME) {
      return sum + t.amount;
    }
    if (
      t.transactionType === TransactionType.EXPENSE ||
      t.transactionType === TransactionType.LOAN_PAYMENT
    ) {
      return sum - t.amount;
    }
    return sum;
  }, 0);
};

export const groupByDate = (transactions: Transaction[]): DateGroup[] => {
  const map = new Map<string, Transaction[]>();
  const order: string[] = [];

  for (const t of transactions) {
    const dateKey = t.transactionDate.slice(0, 10);
    const existing = map.get(dateKey);
    if (existing) {
      existing.push(t);
    } else {
      map.set(dateKey, [t]);
      order.push(dateKey);
    }
  }

  return order.map((dateKey) => {
    const items = map.get(dateKey)!;
    return {
      dateKey,
      transactions: items,
      dailyNet: getDailyNet(items),
    };
  });
};

const SHORT_HEADER_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "2-digit",
  month: "short",
});

export const formatGroupDate = (dateKey: string): string => {
  const date = new Date(`${dateKey}T00:00:00`);
  const parts = SHORT_HEADER_FORMATTER.formatToParts(date);
  const weekday = parts.find((p) => p.type === "weekday")?.value ?? "";
  const day = parts.find((p) => p.type === "day")?.value ?? "";
  const month = parts.find((p) => p.type === "month")?.value ?? "";
  return `${weekday} · ${day} ${month}`;
};

const ROW_DATE_FORMATTER = new Intl.DateTimeFormat("en-GB", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});

export const formatRowDate = (dateKey: string): string => {
  const date = new Date(`${dateKey}T00:00:00`);
  return ROW_DATE_FORMATTER.format(date);
};

export const getAmountVariant = (
  transactionType: TransactionType,
): AmountVariant => {
  if (transactionType === TransactionType.INCOME) {
    return "income";
  }
  if (
    transactionType === TransactionType.EXPENSE ||
    transactionType === TransactionType.LOAN_PAYMENT
  ) {
    return "expense";
  }
  return "neutral";
};

export const getSignedAmount = (transaction: Transaction): number => {
  const variant = getAmountVariant(transaction.transactionType);
  if (variant === "income") {
    return transaction.amount;
  }
  if (variant === "expense") {
    return -transaction.amount;
  }
  return transaction.amount;
};

export const getAmountSign = (
  variant: AmountVariant,
  amount: number,
): string => {
  if (variant === "income" && amount > 0) {
    return "+";
  }
  if (variant === "expense" && amount > 0) {
    return "-";
  }
  return "";
};

export const getPrimaryAccountName = (
  transaction: Transaction,
): string | undefined => {
  if (transaction.transactionType === TransactionType.INCOME) {
    return transaction.toAccountName;
  }
  return transaction.fromAccountName ?? transaction.toAccountName;
};

export const getPrimaryAccountId = (
  transaction: Transaction,
): number | undefined => {
  if (transaction.transactionType === TransactionType.INCOME) {
    return transaction.toAccountId ?? undefined;
  }
  return transaction.fromAccountId ?? transaction.toAccountId ?? undefined;
};

const TYPE_LABELS: Partial<Record<TransactionType, string>> = {
  [TransactionType.INCOME]: "Income",
  [TransactionType.EXPENSE]: "Expense",
  [TransactionType.TRANSFER]: "Transfer",
  [TransactionType.LOAN_PAYMENT]: "Loan",
  [TransactionType.MARKET_UPDATE]: "Market",
  [TransactionType.INVESTMENT]: "Investment",
  [TransactionType.ALL]: "",
};

export const getTypeLabel = (type: TransactionType): string =>
  TYPE_LABELS[type] ?? "";
